import { Card, CircularProgress, Icon } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Field, useField, useFormikContext } from "formik";
import { useEffect, useState } from "react";
import TextError from "utils/TextError";
import Config from "../../../config/index";

// Cookies
import Cookies from "universal-cookie";

const cookies = new Cookies();

const FormFileUpload = ({ label, ...props }) => {
  let vendorUserId = cookies.get("vendorId-for-last-filled");
  let previousvendorUserId = cookies.get("previous-vendorUserId");
  let baseUrl = Config.baseUrl;
  const { setFieldValue, values } = useFormikContext();
  const [field, meta] = useField(props);
  const [filePreview, setFilePreview] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [isPDF, setIsPDF] = useState(false);
  const [showPreviousFileDialog, setShowPreviousFileDialog] = useState(false);

  // Sync the file state with Formik on page change
  useEffect(() => {
    if (field?.value && field.value instanceof File) {
      // Only create the preview if the file is valid
      setFilePreview(URL.createObjectURL(field.value));
      setIsPDF(field.value.type === "application/pdf");
    }
  }, [field?.value]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (file) {
      setFieldValue(field.name, file); // Update Formik state

      // Check if the file is a PDF and create preview accordingly
      if (file.type === "application/pdf") {
        setFilePreview(URL.createObjectURL(file)); // Generate a URL for the file to preview
        setIsPDF(true);
      } else if (file.type.startsWith("image/")) {
        setFilePreview(URL.createObjectURL(file)); // For images, generate preview URL
        setIsPDF(false);
      } else {
        setFilePreview(null); // Clear preview for unsupported file types
        setIsPDF(false);
      }
    } else {
      setFilePreview(null); // If no file is selected, clear preview
      setIsPDF(false);
    }
  };

  const handlePreviewToggle = () => {
    setShowPreview(!showPreview);
  };

  // const handlePreviousFileDialogToggle = () => {
  //   setShowPreviousFileDialog(!showPreviousFileDialog);
  // };

  // const [showPreviousFileDialog, setShowPreviousFileDialog] = useState(false);

  const handlePreviousFileDialogToggle = () => {
    setShowPreviousFileDialog(!showPreviousFileDialog);
    setIsLoading(true); // Reset loading state when dialog is toggled
  };

  const [isLoading, setIsLoading] = useState(true); // Loading state
  const handleIframeLoad = () => {
    setIsLoading(false); // Set loading to false once the iframe has loaded
  };

  const [selectedDocumentUrl, setSelectedDocumentUrl] = useState("");

  const handleDocumentClick = (documentDetails) => {
    fetch(
      `${baseUrl}/vendor/download_center/single_doc?vendorId=${previousvendorUserId}&file_name=${documentDetails?.name}&file_type=${documentDetails?.type}`,
    )
      .then((response) => response.json())
      .then((data) => {
        // If the S3 URL is not a direct PDF link, force it to open as PDF in iframe
        let url = data.url;
        // if (url && !url.endsWith(".pdf")) {
        //   url = `https://docs.google.com/gview?url=${encodeURIComponent(
        //     url
        //   )}&embedded=true`;
        // }
        if (url && url.endsWith("pdf")) {
          url = `https://docs.google.com/gview?url=${encodeURIComponent(
            url,
          )}&embedded=true`;
          setIsPDF(true);
          setSelectedDocumentUrl(url);
        }
        if (
          url.endsWith("jpeg") ||
          url.endsWith("jpg") ||
          url.endsWith("png")
        ) {
          setIsPDF(false);
          setSelectedDocumentUrl(url);
          setTimeout(() => {
            setIsLoading(false);
          }, 1000);
          // Images load faster, so we can set loading to false immediately
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
    handlePreviousFileDialogToggle();
  };

  return (
    <>
      <MDTypography
        variant="h6"
        color="inherit"
        display="flex"
        justifyContent="center"
        float="left"
      >
        {label}{" "}
        {props.notRequired ? "" : <span style={{ color: "red" }}>*</span>}
      </MDTypography>
      <MDBox display="flex" justifyContent="center">
        <Field
          className="choose_file"
          type="file"
          {...field}
          {...props}
          value={undefined}
          onChange={handleFileChange}
        />
        <MDButton
          onClick={handlePreviewToggle}
          style={{ marginTop: "10px" }}
          variant="gradient"
          color="info"
          iconOnly
          size="small"
          type="button"
        >
          <Icon>visibility_icon</Icon>
        </MDButton>
      </MDBox>
      {field?.value?.name && !props.isRejected && (
        <>
          <MDTypography
            color="info"
            variant="subtitle2"
            display="flex"
            justifyContent="center"
            float="left"
          >
            Selected file: {field?.value?.name.slice(0, 14)}
            {field?.value?.name.length > 14 && "..."}
          </MDTypography>
          {/* <div
            style={{ display: "flex", alignItems: "left", marginTop: "1rem" }}
          >
            <MDButton
              onClick={handlePreviewToggle}
              style={{ marginLeft: "10rem", marginTop: "-7rem" }}
              variant="gradient"
              color="info"
              iconOnly
              size="small"
              type="button"
            >
              <Icon>visibility_icon</Icon>
            </MDButton>
          </div> */}
        </>
      )}

      {/* Modal (Dialog) for Preview */}
      <Dialog
        open={showPreview}
        onClose={handlePreviewToggle}
        maxWidth="md"
        fullWidth
        sx={{
          backdropFilter: "blur(5px)",
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.5rem",
            color: "#333",
            textAlign: "center",
            paddingBottom: "16px",
            borderBottom: "2px solid #eee",
          }}
        >
          File Preview
        </DialogTitle>

        <DialogContent
          sx={{ padding: 3, display: "flex", justifyContent: "center" }}
        >
          <Card
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              boxShadow: "0px 8px 25px rgba(0, 0, 0, 0.15)",
              transition: "transform 0.3s ease-in-out",
              "&:hover": {
                transform: "scale(1.02)",
              },
              width: "100%",
              maxWidth: "900px",
            }}
          >
            {isPDF ? (
              <iframe
                src={filePreview}
                title="PDF Preview"
                width="100%"
                height="600px"
                style={{
                  border: "none",
                  borderRadius: "16px",
                  boxShadow: "0px 2px 15px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease-in-out",
                }}
              />
            ) : (
              <img
                src={filePreview}
                alt="Preview"
                width="100%"
                style={{
                  borderRadius: "16px",
                  boxShadow: "0px 2px 15px rgba(0, 0, 0, 0.2)",
                  transition: "all 0.3s ease-in-out",
                }}
              />
            )}
          </Card>
        </DialogContent>

        <DialogActions sx={{ padding: "16px", justifyContent: "center" }}>
          <MDButton
            onClick={handlePreviewToggle}
            color="primary"
            variant="contained"
            sx={{
              fontSize: "1rem",
              padding: "8px 24px",
              backgroundColor: "#007BFF",
              "&:hover": {
                backgroundColor: "#0056b3",
              },
            }}
          >
            Close
          </MDButton>
        </DialogActions>
      </Dialog>
      {field?.value?.name && props.isRejected && (
        <MDTypography mt={1} color="info" variant="subtitle2">
          Selected file: {field?.value?.name}
        </MDTypography>
      )}
      {/* Button to view previous file */}
      {props.isRejected && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "1rem",
            }}
          >
            <MDButton
              onClick={() => handleDocumentClick(field?.value)}
              variant="outlined"
              color="info"
              size="small"
            >
              View Previous File
            </MDButton>
          </div>

          <Dialog
            open={showPreviousFileDialog}
            onClose={handlePreviousFileDialogToggle}
            maxWidth="md"
            fullWidth
            sx={{
              backdropFilter: "blur(5px)",
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: "bold",
                fontSize: "1.5rem",
                color: "#333",
                textAlign: "center",
                paddingBottom: "16px",
                borderBottom: "2px solid #eee",
              }}
            >
              Previous File
            </DialogTitle>

            <DialogContent
              sx={{ padding: 3, display: "flex", justifyContent: "center" }}
            >
              <Card
                sx={{
                  borderRadius: "16px",
                  overflow: "hidden",
                  boxShadow: "0px 8px 25px rgba(0, 0, 0, 0.15)",
                  transition: "transform 0.3s ease-in-out",
                  "&:hover": {
                    transform: "scale(1.02)",
                  },
                  width: "100%",
                  maxWidth: "900px",
                }}
              >
                {isLoading && (
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  >
                    <CircularProgress />
                  </div>
                )}
                {isPDF ? (
                  <iframe
                    src={selectedDocumentUrl}
                    title="Previous File"
                    width="100%"
                    height="900px"
                    style={{
                      border: "none",
                      borderRadius: "16px",
                      boxShadow: "0px 2px 15px rgba(0, 0, 0, 0.2)",
                      transition: "all 0.3s ease-in-out",
                      position: "relative",
                    }}
                    onLoad={handleIframeLoad} // Handle iframe load event
                  />
                ) : (
                  <img
                    src={selectedDocumentUrl}
                    alt="Previous File"
                    width="100%"
                    style={{
                      borderRadius: "16px",
                      boxShadow: "0px 2px 15px rgba(0, 0, 0, 0.2)",
                      transition: "all 0.3s ease-in-out",
                    }}
                  />
                )}
              </Card>
            </DialogContent>

            <DialogActions sx={{ padding: "16px", justifyContent: "center" }}>
              <MDButton
                onClick={handlePreviousFileDialogToggle}
                color="primary"
                variant="contained"
                sx={{
                  fontSize: "1rem",
                  padding: "8px 24px",
                  backgroundColor: "#007BFF",
                  "&:hover": {
                    backgroundColor: "#0056b3",
                  },
                }}
              >
                Close
              </MDButton>
            </DialogActions>
          </Dialog>
        </>
      )}

      {meta.touched && meta.error && <TextError msg={meta.error} />}
    </>
  );
};

export default FormFileUpload;
